import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import PayPalSubscriptionButton from '../components/PayPalSubscriptionButton';
import { trackPricingView, trackCheckoutStarted } from '../services/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// East African countries that should use Pesapal
const PESAPAL_COUNTRIES = ['TZ', 'KE', 'UG', 'RW', 'BI'];

const Pricing = () => {
  const { user } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [emailCaptured, setEmailCaptured] = useState(false);
  const [userCountry, setUserCountry] = useState(null);
  const [gateway, setGateway] = useState('paypal'); // 'paypal' or 'pesapal'
  const [showPesapalModal, setShowPesapalModal] = useState(false);
  const [showPayPalModal, setShowPayPalModal] = useState(false);
  const [pesapalForm, setPesapalForm] = useState({ email: '', phone: '', name: '' });
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  // Detect user's country on mount
  useEffect(() => {
    detectUserCountry();
    // Track pricing page view
    trackPricingView(document.referrer ? 'referrer' : 'direct');
  }, []);

  const detectUserCountry = async () => {
    try {
      // Use free IP geolocation API
      const response = await axios.get('https://ipapi.co/json/', { timeout: 5000 });
      const country = response.data?.country_code;
      setUserCountry(country);
      
      // Auto-select gateway based on location
      if (PESAPAL_COUNTRIES.includes(country)) {
        setGateway('pesapal');
      } else {
        setGateway('paypal'); // Use PayPal for global payments
      }
    } catch (error) {
      console.log('Could not detect country, defaulting to PayPal');
      setGateway('paypal');
    }
  };

  const handleEmailCapture = async (e) => {
    e.preventDefault();
    if (!email) return;

    try {
      await axios.post(`${API_URL}/api/auth/capture-email`, { email });
      setEmailCaptured(true);
      setTimeout(() => setEmailCaptured(false), 3000);
    } catch (error) {
      console.error('Failed to capture email:', error);
    }
  };

  const handleSelectPlan = (planType) => {
    // Track checkout started
    const price = planType === 'student' ? 4 : (planType === 'teacher' ? 6 : 20);
    trackCheckoutStarted(planType, gateway, price);
    
    if (gateway === 'pesapal') {
      // Show Pesapal payment modal
      setSelectedPlan(planType);
      setPesapalForm({
        email: user?.email || '',
        phone: '',
        name: user?.name || ''
      });
      setShowPesapalModal(true);
    } else {
      // PayPal flow - show PayPal modal for all paid plans
      if (planType === 'student' || planType === 'teacher' || planType === 'group') {
        setSelectedPlan(planType);
        setShowPayPalModal(true);
      }
    }
  };

  const handlePayPalSuccess = (data) => {
    console.log('PayPal subscription successful:', data);
    setShowPayPalModal(false);
    navigate(`/payment-success?gateway=paypal&plan=${data.planType}&subscription_id=${data.subscriptionId}`);
  };

  const handlePayPalError = (error) => {
    console.error('PayPal error:', error);
    const errorMessage = error?.message || 'Payment failed. Please try again.';
    alert(errorMessage);
  };

  const handlePesapalPayment = async () => {
    if (!pesapalForm.email || !pesapalForm.phone || !pesapalForm.name) {
      alert('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/pesapal/create-payment`, {
        plan: selectedPlan,
        email: pesapalForm.email,
        phone: pesapalForm.phone,
        customer_name: pesapalForm.name
      });

      if (response.data.success && response.data.redirect_url) {
        // Redirect to Pesapal payment page
        window.location.href = response.data.redirect_url;
      } else {
        alert('Failed to create payment. Please try again.');
      }
    } catch (error) {
      console.error('Pesapal payment error:', error);
      alert(error.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // USD Plans (Hotmart - Global)
  const usdPlans = [
    {
      id: 'free',
      name: 'Free Plan',
      icon: '✨',
      subtitle: 'Get Started',
      tagline: 'Perfect for trying out',
      price: 0,
      currency: 'USD',
      period: 'forever',
      features: [
        '3 quizzes per day',
        'Text input only',
        'Up to 7 questions per quiz',
        'All exam presets',
        '5,000 character limit',
      ],
      limitations: [
        'No PDF, URL, Image, Audio',
        'No AI Question Solver',
        'No History & Analytics',
        'No Practice Mistakes',
      ],
      popular: false,
      color: 'gray',
      isFree: true
    },
    {
      id: 'student',
      name: 'Student Plan',
      icon: '🎓',
      subtitle: 'Most Popular',
      tagline: 'For personal study and exam prep',
      price: 4.99,
      currency: 'USD',
      period: '/month',
      features: [
        'Unlimited quiz generation',
        'All input types (Text, URL, PDF, Image, Audio)',
        'AI Question Solver',
        'All exam presets (NECTA, IGCSE, A-Level, SAT, etc.)',
        'Up to 20 questions per quiz',
        'Practice My Mistakes feature',
        'Personal history & analytics',
      ],
      popular: true,
      color: 'indigo'
    },
    {
      id: 'teacher',
      name: 'Teacher Plan',
      icon: '👩‍🏫',
      subtitle: 'For Educators',
      tagline: 'Built for classroom support',
      price: 9.99,
      currency: 'USD',
      period: '/month',
      features: [
        'Everything in Student Plan',
        'Create and manage classes',
        'Assign quizzes with due dates',
        'Track student progress',
        'PDF export (no watermark)',
        'Shareable quiz links',
        'Class analytics',
      ],
      popular: false,
      color: 'emerald'
    },
    {
      id: 'group',
      name: 'Group Plan',
      icon: '🏫',
      subtitle: 'For Schools',
      tagline: '5 teacher seats included',
      price: 420,
      currency: 'USD',
      period: '/year',
      yearlyNote: 'Billed annually',
      features: [
        'Everything in Teacher Plan',
        '5 teacher seats included',
        'Group admin dashboard',
        'Invite and manage teachers',
        'Buy additional seats anytime',
        'Shared analytics',
        'Priority support',
      ],
      popular: false,
      color: 'purple'
    }
  ];

  // TZS Plans (Pesapal - Tanzania/East Africa)
  const tzsPlans = [
    {
      id: 'free',
      name: 'Free Plan',
      icon: '✨',
      subtitle: 'Get Started',
      tagline: 'Perfect for trying out',
      price: 0,
      currency: 'TZS',
      period: 'forever',
      features: [
        '3 quizzes per day',
        'Text input only',
        'Up to 7 questions per quiz',
        'All exam presets',
        '5,000 character limit',
      ],
      limitations: [
        'No PDF, URL, Image, Audio',
        'No AI Question Solver',
        'No History & Analytics',
        'No Practice Mistakes',
      ],
      popular: false,
      color: 'gray',
      isFree: true
    },
    {
      id: 'student',
      name: 'Student Plan',
      icon: '🎓',
      subtitle: 'Most Popular',
      tagline: 'For personal study and exam prep',
      price: 6000,
      currency: 'TZS',
      period: '/month',
      features: [
        'Unlimited quiz generation',
        'All input types (Text, URL, PDF, Image, Audio)',
        'AI Question Solver',
        'All exam presets (NECTA, CSEE, ACSEE, etc.)',
        'Up to 20 questions per quiz',
        'Practice My Mistakes feature',
        'Personal history & analytics',
      ],
      popular: true,
      color: 'indigo'
    },
    {
      id: 'teacher',
      name: 'Teacher Plan',
      icon: '👩‍🏫',
      subtitle: 'For Educators',
      tagline: 'Built for classroom support',
      price: 15000,
      currency: 'TZS',
      period: '/month',
      features: [
        'Everything in Student Plan',
        'Create and manage classes',
        'Assign quizzes with due dates',
        'Track student progress',
        'PDF export (no watermark)',
        'Shareable quiz links',
        'Class analytics',
      ],
      popular: false,
      color: 'emerald'
    },
    {
      id: 'group',
      name: 'Group Plan',
      icon: '🏫',
      subtitle: 'For Schools',
      tagline: '5 teacher seats included',
      price: 540000,
      currency: 'TZS',
      period: '/year',
      yearlyNote: 'Billed annually',
      features: [
        'Everything in Teacher Plan',
        '5 teacher seats included',
        'Group admin dashboard',
        'Invite and manage teachers',
        'Buy additional seats anytime',
        'Shared analytics',
        'Priority support',
      ],
      popular: false,
      color: 'purple'
    }
  ];

  // Get current plans based on selected gateway
  const plans = gateway === 'pesapal' ? tzsPlans : usdPlans;

  const formatPrice = (price, currency) => {
    if (currency === 'TZS') {
      return `TSh ${price.toLocaleString()}`;
    }
    return `$${price}`;
  };

  // Comparison features for the table
  const comparisonFeatures = [
    { name: 'Quiz Generation', free: '3/day', student: 'Unlimited', teacher: 'Unlimited', group: 'Unlimited' },
    { name: 'Input Types', free: 'Text only', student: 'All', teacher: 'All', group: 'All' },
    { name: 'AI Question Solver', free: '✗', student: '✓', teacher: '✓', group: '✓' },
    { name: 'Questions per Quiz', free: 'Max 7', student: 'Up to 20', teacher: 'Up to 20', group: 'Up to 20' },
    { name: 'Practice My Mistakes', free: '✗', student: '✓', teacher: '✓', group: '✓' },
    { name: 'History & Analytics', free: '✗', student: '✓', teacher: '✓', group: '✓' },
    { name: 'PDF Export', free: '✗', student: 'Watermark', teacher: 'No watermark', group: 'No watermark' },
    { name: 'My Classes', free: '✗', student: '✗', teacher: '✓', group: '✓' },
    { name: 'Assign Quizzes', free: '✗', student: '✗', teacher: '✓', group: '✓' },
    { name: 'Teacher Seats', free: '—', student: '1', teacher: '1', group: '5' },
    { name: 'Priority Support', free: '✗', student: '✗', teacher: '✗', group: '✓' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Navigation */}
      <nav className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <button onClick={() => navigate('/')} className="flex items-center">
              <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#b8e1f5]">
                <img src="/logo.png" alt="Study Quiz" className="w-10 h-10 object-cover" />
              </div>
              <span className="ml-3 text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Study Quiz
              </span>
            </button>
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {isDarkMode ? (
                  <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                )}
              </button>
              {user ? (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Dashboard
                </button>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block mb-4 px-4 py-2 bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 rounded-full">
            <span className="text-amber-800 dark:text-amber-300 font-semibold text-sm">
              ⚡ Early Access Pricing — Lock in your rate for life!
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Choose Your Plan
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Early Access pricing is temporary and will increase as new features roll out.
          </p>
        </div>

        {/* Payment Gateway Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center bg-white dark:bg-gray-800 rounded-xl p-1.5 shadow-lg border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setGateway('paypal')}
              className={`flex items-center px-5 py-2.5 rounded-lg font-medium text-sm transition-all ${
                gateway === 'paypal'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.641.641 0 0 1 .632-.541h6.964c2.075 0 3.736.68 4.938 2.02 1.135 1.267 1.56 2.926 1.264 4.93-.016.106-.036.217-.058.333-.45 2.36-1.59 4.227-3.392 5.555-1.705 1.258-3.818 1.897-6.277 1.897H7.447l-.371 3.423zm11.785-15.2c-.057-.055-.117-.109-.18-.161-.164-.138-.347-.263-.548-.375a6.082 6.082 0 0 0-.66-.31 8.348 8.348 0 0 0-1.032-.317c-.392-.096-.816-.165-1.271-.207a13.641 13.641 0 0 0-1.51-.068H7.22l-2.16 13.496h3.614c.21 0 .424-.005.642-.015.217-.01.438-.026.663-.048.225-.022.452-.052.682-.089.23-.037.457-.082.68-.135.658-.157 1.267-.379 1.827-.667.56-.288 1.05-.636 1.468-1.045.418-.409.76-.88 1.027-1.414.266-.533.45-1.122.55-1.769.059-.378.07-.738.033-1.08a2.96 2.96 0 0 0-.386-1.106 2.741 2.741 0 0 0-.399-.49z"/>
              </svg>
              PayPal (USD)
            </button>
            <button
              onClick={() => setGateway('pesapal')}
              className={`flex items-center px-5 py-2.5 rounded-lg font-medium text-sm transition-all ${
                gateway === 'pesapal'
                  ? 'bg-green-600 text-white shadow-md'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              Pesapal (TZS)
            </button>
          </div>
        </div>

        {/* Location-based pricing notice */}
        {gateway === 'pesapal' && (
          <div className="text-center mb-6">
            <span className="inline-flex items-center px-4 py-2 bg-green-100 dark:bg-green-900/30 rounded-full text-sm text-green-700 dark:text-green-300">
              <span className="mr-2">📍</span> Local pricing for Tanzania - Pay via M-Pesa, Tigo Pesa & more
            </span>
          </div>
        )}

        {gateway === 'paypal' && (
          <div className="text-center mb-6">
            <span className="inline-flex items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-full text-sm text-blue-700 dark:text-blue-300">
              <span className="mr-2">🌍</span> Global pricing - Pay with PayPal, Credit/Debit Card
            </span>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden transition-all hover:shadow-2xl ${
                plan.popular ? 'ring-2 ring-indigo-600 dark:ring-indigo-400' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-center py-2 text-sm font-bold">
                  ⭐ Most Popular
                </div>
              )}

              <div className={`p-8 ${plan.popular ? 'pt-14' : ''}`}>
                {/* Plan Header */}
                <div className="text-center mb-6">
                  <div className="text-5xl mb-3">{plan.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{plan.name}</h3>
                  <p className="text-sm text-indigo-600 dark:text-indigo-400 font-medium">{plan.subtitle}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{plan.tagline}</p>
                </div>

                {/* Price */}
                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      {formatPrice(plan.price, plan.currency)}
                    </span>
                    <span className="text-gray-600 dark:text-gray-400 ml-1">{plan.period}</span>
                  </div>
                  {plan.yearlyNote && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{plan.yearlyNote}</p>
                  )}
                  {gateway === 'pesapal' && (
                    <div className="mt-2 inline-flex items-center px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded text-xs text-green-700 dark:text-green-300">
                      <span>Pay via M-Pesa, Tigo Pesa, Airtel Money</span>
                    </div>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-4">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start text-sm">
                      <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Limitations (for Free plan) */}
                {plan.limitations && (
                  <ul className="space-y-2 mb-6 pt-3 border-t border-gray-200 dark:border-gray-700">
                    {plan.limitations.map((limitation, index) => (
                      <li key={index} className="flex items-start text-sm">
                        <span className="text-gray-400 mr-2">🔒</span>
                        <span className="text-gray-500 dark:text-gray-400">{limitation}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* CTA Button */}
                {plan.isFree ? (
                  <button
                    onClick={() => navigate('/register?plan=free')}
                    className="w-full py-3 px-6 rounded-lg font-bold transition-all bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Get Started Free
                  </button>
                ) : (
                  <button
                    onClick={() => handleSelectPlan(plan.id)}
                    className={`w-full py-3 px-6 rounded-lg font-bold transition-all ${
                      gateway === 'pesapal' 
                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700'
                        : plan.popular
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-lg'
                          : plan.color === 'emerald'
                            ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {gateway === 'pesapal' 
                      ? `Pay ${formatPrice(plan.price, 'TZS')}` 
                      : plan.id === 'group' 
                        ? `Subscribe $420/yr` 
                        : `Subscribe $${plan.price}/mo`}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Comparison Table */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-8">
            Compare Plans
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="px-4 py-4 text-left text-sm font-bold text-gray-900 dark:text-white">Feature</th>
                  <th className="px-4 py-4 text-center text-sm font-bold text-gray-900 dark:text-white">
                    ✨ Free<br/>
                    <span className="text-gray-500 dark:text-gray-400">$0</span>
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-bold text-gray-900 dark:text-white">
                    🎓 Student<br/>
                    <span className="text-indigo-600 dark:text-indigo-400">
                      {gateway === 'pesapal' ? 'TSh 6,000/mo' : '$4.99/mo'}
                    </span>
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-bold text-gray-900 dark:text-white">
                    👩‍🏫 Teacher<br/>
                    <span className="text-emerald-600 dark:text-emerald-400">
                      {gateway === 'pesapal' ? 'TSh 15,000/mo' : '$9.99/mo'}
                    </span>
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-bold text-gray-900 dark:text-white">
                    🏫 Group<br/>
                    <span className="text-purple-600 dark:text-purple-400">
                      {gateway === 'pesapal' ? 'TSh 540,000/yr' : '$420/year'}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {comparisonFeatures.map((feature, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/50' : ''}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{feature.name}</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-700 dark:text-gray-300">
                      {feature.free === '✓' ? (
                        <span className="text-green-500">✓</span>
                      ) : feature.free === '✗' ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        feature.free
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-700 dark:text-gray-300">
                      {feature.student === '✓' ? (
                        <span className="text-green-500">✓</span>
                      ) : feature.student === '✗' ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        feature.student
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-700 dark:text-gray-300">
                      {feature.teacher === '✓' ? (
                        <span className="text-green-500">✓</span>
                      ) : feature.teacher === '✗' ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        feature.teacher
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-700 dark:text-gray-300">
                      {feature.group === '✓' ? (
                        <span className="text-green-500">✓</span>
                      ) : feature.group === '✗' ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        feature.group
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Email Capture Section */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 md:p-12 text-center text-white mb-12">
          <h2 className="text-3xl font-bold mb-4">
            Stay Updated on New Features
          </h2>
          <p className="text-lg mb-6 opacity-90">
            Get early access to new exam presets, features, and exclusive discounts
          </p>

          <form onSubmit={handleEmailCapture} className="max-w-md mx-auto">
            <div className="flex gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
                required
              />
              <button
                type="submit"
                className="px-6 py-3 bg-white text-indigo-600 rounded-lg font-medium hover:bg-gray-100 transition-all"
              >
                Notify Me
              </button>
            </div>
            {emailCaptured && (
              <p className="mt-3 text-sm text-green-200">
                ✓ Thanks! We'll keep you updated.
              </p>
            )}
          </form>
        </div>

        {/* FAQ */}
        <div className="text-center">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Questions?</h3>
          <p className="text-gray-600 dark:text-gray-400">
            {gateway === 'pesapal' 
              ? 'Pay easily with M-Pesa, Tigo Pesa, Airtel Money, or Bank Card.' 
              : 'All plans include a free trial through Hotmart.'
            }{' '}
            <button
              onClick={() => navigate('/dashboard')}
              className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
            >
              Contact Support
            </button>
            {' '}for help choosing the right plan.
          </p>
        </div>
      </div>

      {/* Pesapal Payment Modal */}
      {showPesapalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowPesapalModal(false)}>
          <div 
            className={`max-w-md w-full rounded-2xl shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-white">Complete Payment</h3>
                  <p className="text-green-100 mt-1">
                    {selectedPlan && tzsPlans.find(p => p.id === selectedPlan)?.name}
                  </p>
                </div>
                <button 
                  onClick={() => setShowPesapalModal(false)}
                  className="text-white/80 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mt-4 flex items-baseline">
                <span className="text-3xl font-bold text-white">
                  {selectedPlan && formatPrice(tzsPlans.find(p => p.id === selectedPlan)?.price, 'TZS')}
                </span>
                <span className="text-green-100 ml-2">
                  {selectedPlan && tzsPlans.find(p => p.id === selectedPlan)?.period}
                </span>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={pesapalForm.name}
                    onChange={(e) => setPesapalForm({...pesapalForm, name: e.target.value})}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={pesapalForm.email}
                    onChange={(e) => setPesapalForm({...pesapalForm, email: e.target.value})}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="you@example.com"
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Phone Number (M-Pesa/Tigo Pesa) *
                  </label>
                  <input
                    type="tel"
                    value={pesapalForm.phone}
                    onChange={(e) => setPesapalForm({...pesapalForm, phone: e.target.value})}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="+255 7XX XXX XXX"
                    required
                  />
                  <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Include country code (e.g., +255 for Tanzania)
                  </p>
                </div>
              </div>

              {/* Payment Methods */}
              <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <p className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Accepted Payment Methods:
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded-full text-xs font-medium">
                    M-Pesa
                  </span>
                  <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                    Tigo Pesa
                  </span>
                  <span className="px-3 py-1 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-full text-xs font-medium">
                    Airtel Money
                  </span>
                  <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">
                    Bank Card
                  </span>
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={handlePesapalPayment}
                disabled={loading}
                className="w-full mt-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-bold hover:from-green-700 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-3 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Proceed to Payment
                  </>
                )}
              </button>

              <p className={`mt-4 text-center text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Secured by Pesapal. You'll be redirected to complete payment.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* PayPal Payment Modal */}
      {showPayPalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowPayPalModal(false)}>
          <div 
            className={`max-w-md w-full rounded-2xl shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-white">Subscribe with PayPal</h3>
                  <p className="text-blue-100 mt-1">
                    {selectedPlan === 'student' ? 'Student Plan' : selectedPlan === 'teacher' ? 'Teacher Plan' : 'Group Plan'}
                  </p>
                </div>
                <button 
                  onClick={() => setShowPayPalModal(false)}
                  className="text-white/80 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mt-4 flex items-baseline">
                <span className="text-3xl font-bold text-white">
                  ${selectedPlan === 'student' ? '4.99' : selectedPlan === 'teacher' ? '9.99' : '420'}
                </span>
                <span className="text-blue-100 ml-2">{selectedPlan === 'group' ? '/year' : '/month'}</span>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {selectedPlan === 'group' ? (
                    <>
                      ✓ 5 teacher seats included<br/>
                      ✓ Automatic yearly billing<br/>
                      ✓ Cancel anytime<br/>
                      ✓ Instant activation
                    </>
                  ) : (
                    <>
                      ✓ Automatic monthly billing<br/>
                      ✓ Cancel anytime<br/>
                      ✓ Instant activation
                    </>
                  )}
                </p>
              </div>

              {/* Enter email for non-logged in users */}
              {!user && (
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Your Email
                  </label>
                  <input
                    type="email"
                    value={pesapalForm.email}
                    onChange={(e) => setPesapalForm({...pesapalForm, email: e.target.value})}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="you@example.com"
                    required
                  />
                </div>
              )}

              {/* PayPal Button */}
              <PayPalSubscriptionButton
                planType={selectedPlan}
                userEmail={user?.email || pesapalForm.email}
                userName={user?.name || pesapalForm.name || 'User'}
                onSuccess={handlePayPalSuccess}
                onError={handlePayPalError}
                onCancel={() => setShowPayPalModal(false)}
              />

              <p className={`mt-4 text-center text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Secured by PayPal. Your subscription will auto-renew monthly.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;
